import { useEffect, useRef, useState } from "react";

// Capture ownership stays here; an explicitly connected room may send tracks.
export default function MeetingDevicePreview({ onTracks, sharing = false }) {
  const video = useRef(null);
  const tracks = useRef({ audio: null, video: null });
  const generation = useRef(0);
  const pending = useRef(false);
  const mounted = useRef(false);
  const [active, setActive] = useState({ audio: false, video: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function attachPreview() {
    onTracks?.({ ...tracks.current });
    if (video.current) {
      video.current.srcObject = tracks.current.video
        ? new MediaStream([tracks.current.video]) : null;
    }
  }
  function stopAll() {
    generation.current += 1;
    pending.current = false;
    for (const kind of ["audio", "video"]) {
      const track = tracks.current[kind];
      tracks.current[kind] = null;
      if (track) { track.onended = null; track.stop(); }
    }
    attachPreview();
    if (mounted.current) {
      setActive({ audio: false, video: false }); setBusy(false);
    }
  }
  useEffect(() => {
    mounted.current = true;
    const stopOnExit = () => stopAll();
    window.addEventListener("pagehide", stopOnExit);
    return () => {
      mounted.current = false; stopAll();
      window.removeEventListener("pagehide", stopOnExit);
    };
  // Capture ownership and cleanup are intentionally tied to this component.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggle(kind) {
    const existing = tracks.current[kind];
    if (existing) {
      existing.onended = null; existing.stop(); tracks.current[kind] = null;
      attachPreview(); setActive((previous) => ({ ...previous, [kind]: false }));
      return;
    }
    if (pending.current) return;
    setError("");
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setError("Camera and microphone access requires a supported browser and a secure connection."); return;
    }
    const current = ++generation.current;
    pending.current = true; setBusy(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: kind === "audio" ? { echoCancellation: true, noiseSuppression: true } : false,
        video: kind === "video" ? { width: { ideal: 640 }, height: { ideal: 360 },
          frameRate: { ideal: 24, max: 30 } } : false,
      });
      if (!mounted.current || current !== generation.current) {
        stream.getTracks().forEach((track) => track.stop()); return;
      }
      const track = stream.getTracks().find((item) => item.kind === kind);
      stream.getTracks().filter((item) => item !== track).forEach((item) => item.stop());
      if (!track || track.readyState !== "live") {
        stream.getTracks().forEach((item) => item.stop());
        throw new Error("Device is unavailable.");
      }
      tracks.current[kind] = track;
      track.onended = () => {
        if (tracks.current[kind] !== track) return;
        tracks.current[kind] = null; attachPreview();
        if (mounted.current) setActive((previous) => ({ ...previous, [kind]: false }));
      };
      attachPreview(); setActive((previous) => ({ ...previous, [kind]: true }));
    } catch (failure) {
      if (mounted.current && current === generation.current) {
        setError(failure.name === "NotAllowedError" ? "Device permission was not granted. You can allow it in your browser."
          : failure.name === "NotFoundError" ? "No matching camera or microphone was found."
          : failure.name === "NotReadableError" ? "The device is unavailable or being used by another application."
          : "Unable to start this device. Check your browser and device settings.");
      }
    } finally {
      if (mounted.current && current === generation.current) {
        pending.current = false; setBusy(false);
      }
    }
  }
  return <section className="space-y-3 rounded-xl border p-4" aria-label="Camera and microphone preview">
    <h3 className="font-medium">Camera &amp; microphone</h3>
    <p className="text-sm text-slate-600">{sharing ? "Active devices may be shared with admitted meeting participants." : "Local preview only. Connect audio & video to share active devices."}</p>
    <video ref={video} autoPlay playsInline muted aria-label="Your camera preview"
      className="aspect-video w-full rounded-lg bg-slate-900" />
    {error && <p role="alert" className="text-sm">{error}</p>}
    <div className="flex flex-wrap gap-2">
      <button type="button" aria-pressed={active.video} disabled={busy} onClick={() => toggle("video")}
        className="rounded border px-3 py-2">{active.video ? "Stop camera" : "Start camera"}</button>
      <button type="button" aria-pressed={active.audio} disabled={busy} onClick={() => toggle("audio")}
        className="rounded border px-3 py-2">{active.audio ? "Stop microphone" : "Start microphone"}</button>
      <button type="button" onClick={stopAll} className="rounded border px-3 py-2">Stop all devices</button>
    </div>
    <p role="status" className="text-xs">Camera: {active.video ? "on" : "off"} · Microphone: {active.audio ? "on" : "off"}</p>
  </section>;
}
